import {
  Clock,
  Cpu,
  Globe,
  HardDrive,
  MemoryStick,
  Network,
  Shield,
  Users,
  Zap,
} from "lucide-react";
import React, { useState } from "react";
import "./vps.css";

interface VPSFeature {
  icon: React.ElementType;
  label: string;
  value: string;
}

interface VPSPlan {
  id: string;
  name: string;
  description: string;
  color: string;
  features: VPSFeature[];
  price: number;
  popular?: boolean;
  outOfStock?: boolean;
  notAvailable?: boolean;
  type?: "linux" | "gaming" | "nextcloud";
}

interface OSVersion {
  id: string;
  label: string;
  outOfStock?: boolean;
  notAvailable?: boolean;
}

interface OSCategory {
  id: string;
  name: string;
  icon: string;
  versions: OSVersion[];
}

const VPS: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState("debian");
  const [selectedVersion, setSelectedVersion] = useState("debian12");
  const [selectedType, setSelectedType] = useState<
    "linux" | "gaming" | "nextcloud"
  >("linux");

  const osCategories: OSCategory[] = [
    {
      id: "debian",
      name: "Debian",
      icon: "debian",
      versions: [
        { id: "debian10", label: "Debian 10", notAvailable: false },
        { id: "debian11", label: "Debian 11", notAvailable: false },
        { id: "debian12", label: "Debian 12", notAvailable: false },
        { id: "debian13", label: "Debian 13", notAvailable: true },
      ],
    },
    {
      id: "ubuntu",
      name: "Ubuntu",
      icon: "ubuntu",
      versions: [
        { id: "ubuntu20", label: "Ubuntu 20.04", notAvailable: false },
        { id: "ubuntu22", label: "Ubuntu 22.04", notAvailable: false },
      ],
    },
    {
      id: "windows",
      name: "Windows",
      icon: "windows",
      versions: [
        {
          id: "windows2019",
          label: "Windows Server 2019",
          notAvailable: false,
        },
        { id: "windows2022", label: "Windows Server 2022", notAvailable: true },
      ],
    },
  ];

  const vpsPlans: VPSPlan[] = [
    {
      id: "gaming",
      name: "VPS Gaming Lite",
      description: "Optimisé pour serveurs de jeux et streaming",
      color: "yellow",
      price: 8.99,
      type: "gaming",
      notAvailable: true,
      features: [
        { icon: Cpu, label: "CPU", value: "1 vCPU Gaming" },
        { icon: MemoryStick, label: "RAM", value: "4 Go" },
        { icon: HardDrive, label: "Stockage", value: "25 Go NVMe" },
        { icon: Network, label: "Bande passante", value: "10 GB/s" },
        { icon: Globe, label: "IPv4", value: "2 adresses" },
        { icon: Zap, label: "Latence", value: "< 10ms" },
        { icon: Users, label: "Slots", value: "Jusqu'à 100 joueurs" },
      ],
    },
    {
      id: "lite",
      name: "VPS Lite",
      description: "Parfait pour débuter avec un petit projet",
      color: "green",
      price: 3.4,
      notAvailable: true,
      type: "linux",
      features: [
        { icon: Cpu, label: "CPU", value: "1 vCPU" },
        { icon: MemoryStick, label: "RAM", value: "2 Go" },
        { icon: HardDrive, label: "Stockage", value: "20 Go Stockage" },
        { icon: Network, label: "Bande passante", value: "10 GB/s" },
        {
          icon: Globe,
          label: "IPv4",
          value: "1 adress (1 ip supplementaire 1,50€)",
        },
        { icon: Shield, label: "Anti-DDoS", value: "Protection incluse" },
        { icon: Clock, label: "Uptime", value: "99.9% garanti" },
      ],
    },
    {
      id: "starter",
      name: "VPS Starter",
      description: "Idéal pour applications moyennes et sites web",
      color: "blue",
      price: 5.5,
      popular: true,
      notAvailable: true,
      type: "linux",
      features: [
        { icon: Cpu, label: "CPU", value: "2 vCPU" },
        { icon: MemoryStick, label: "RAM", value: "4 Go" },
        { icon: HardDrive, label: "Stockage", value: "40 Go Stockage" },
        { icon: Network, label: "Bande passante", value: "10 GB/s" },
        {
          icon: Globe,
          label: "IPv4",
          value: "1 adresse (IP additionnelle 1,50 €)",
        },
        { icon: Shield, label: "Anti-DDoS", value: "" },
        { icon: Clock, label: "Uptime", value: "99.95% garanti" },
      ],
    },
    {
      id: "pro",
      name: "VPS Pro",
      description: "Pour applications professionnelles exigeantes",
      color: "purple",
      price: 8.99,
      notAvailable: true,
      type: "linux",
      features: [
        { icon: Cpu, label: "CPU", value: "4 vCPU" },
        { icon: MemoryStick, label: "RAM", value: "8 Go" },
        { icon: HardDrive, label: "Stockage", value: "80 Go Stockage" },
        { icon: Network, label: "Bande passante", value: "10 GB/s" },
        {
          icon: Globe,
          label: "IPv4",
          value: "1 adresses (IP additionnelle 1,50 €)",
        },
        { icon: Shield, label: "Anti-DDoS", value: "" },
        { icon: Clock, label: "Uptime", value: "99.99% garanti" },
      ],
    },
    {
      id: "ultra",
      name: "VPS Ultra",
      description: "Solution haute performance pour entreprises",
      color: "orange",
      price: 11.5,
      notAvailable: true,
      type: "linux",
      features: [
        { icon: Cpu, label: "CPU", value: "6 vCPU" },
        { icon: MemoryStick, label: "RAM", value: "16 Go" },
        { icon: HardDrive, label: "Stockage", value: "120 Go Stockage" },
        { icon: Network, label: "Bande passante", value: "10 GB/s" },
        {
          icon: Globe,
          label: "IPv4",
          value: "1 adresses (IP additionnelle 1,50 €)",
        },
        { icon: Shield, label: "Anti-DDoS", value: "" },
        { icon: Clock, label: "Uptime", value: "99.99% garanti" },
      ],
    },
    {
      id: "Premium",
      name: "VPS Premium",
      description: "Solution haute performance pour tous",
      color: "orange",
      price: 16.99,
      notAvailable: true,
      type: "linux",
      features: [
        { icon: Cpu, label: "CPU", value: "6 vCPU" },
        { icon: MemoryStick, label: "RAM", value: "24 Go" },
        { icon: HardDrive, label: "Stockage", value: "160 Go Stockage" },
        { icon: Network, label: "Bande passante", value: "10 GB/s" },
        {
          icon: Globe,
          label: "IPv4",
          value: "1 adresses (IP additionnelle 1,50 €)",
        },
        { icon: Shield, label: "Anti-DDoS", value: "" },
        { icon: Clock, label: "Uptime", value: "99.99% garanti" },
      ],
    },
    {
      id: "ultimate",
      name: "VPS Ultimate",
      description: "Performance maximale pour charges critiques",
      color: "red",
      price: 22,
      notAvailable: true,
      type: "linux",
      features: [
        { icon: Cpu, label: "CPU", value: "8 vCPU" },
        { icon: MemoryStick, label: "RAM", value: "32 Go" },
        { icon: HardDrive, label: "Stockage", value: "220 Go Stockage" },
        { icon: Network, label: "Bande passante", value: "Illimitée" },
        {
          icon: Globe,
          label: "IPv4",
          value: "1 adresses (IP additionnelle 1,50 €)",
        },
        { icon: Shield, label: "Anti-DDoS", value: "" },
        { icon: Clock, label: "Uptime", value: "99.99% garanti" },
      ],
    },
    {
      id: "gaming1",
      name: "VPS Gaming Starter",
      description: "Pour débuter le jeu en ligne",
      color: "yellow",
      price: 10.99,
      notAvailable: true,
      type: "gaming",
      features: [
        { icon: Cpu, label: "CPU", value: "4 vCPU Gaming" },
        { icon: MemoryStick, label: "RAM", value: "8 Go" },
        { icon: HardDrive, label: "Stockage", value: "120 Go NVMe" },
        { icon: Network, label: "Bande passante", value: "10 GB/s" },
        { icon: Globe, label: "IPv4", value: "1 adresse" },
        { icon: Zap, label: "Latence", value: "< 10ms" },
        { icon: Users, label: "Slots", value: "Jusqu'à 50 joueurs" },
      ],
    },
    {
      id: "gaming2",
      name: "VPS Gaming Pro",
      description: "Pour serveurs de jeux exigeants",
      color: "yellow",
      price: 14.99,
      notAvailable: true,
      type: "gaming",
      features: [
        { icon: Cpu, label: "CPU", value: "8 vCPU Gaming" },
        { icon: MemoryStick, label: "RAM", value: "16 Go" },
        { icon: HardDrive, label: "Stockage", value: "240 Go Stockage" },
        { icon: Network, label: "Bande passante", value: "10 GB/s" },
        { icon: Globe, label: "IPv4", value: "2 adresses" },
        { icon: Zap, label: "Latence", value: "< 5ms" },
        { icon: Users, label: "Slots", value: "Jusqu'à 150 joueurs" },
      ],
    },
    {
      id: "nextcloud1",
      name: "VPS Nextcloud Starter",
      description: "Stockage cloud privé clé en main",
      color: "blue",
      price: 5.99,
      notAvailable: true,
      type: "nextcloud",
      features: [
        { icon: Cpu, label: "CPU", value: "2 vCPU" },
        { icon: MemoryStick, label: "RAM", value: "4 Go" },
        { icon: HardDrive, label: "Stockage", value: "100 Go Stockage" },
        { icon: Network, label: "Bande passante", value: "10 GB/s" },
        { icon: Globe, label: "IPv4", value: "1 adresse" },
        { icon: Shield, label: "Anti-DDoS", value: "" },
      ],
    },
    {
      id: "nextcloud2",
      name: "VPS Nextcloud Pro",
      description: "Pour un cloud d'équipe performant",
      color: "blue",
      price: 10.99,
      notAvailable: true,
      type: "nextcloud",
      features: [
        { icon: Cpu, label: "CPU", value: "4 vCPU" },
        { icon: MemoryStick, label: "RAM", value: "8 Go" },
        { icon: HardDrive, label: "Stockage", value: "250 Go Stockage" },
        { icon: Network, label: "Bande passante", value: "10 GB/s" },
        { icon: Globe, label: "IPv4", value: "1 adresse" },
        { icon: Shield, label: "Anti-DDoS", value: "" },
      ],
    },
  ];

  const filteredOSCategories =
    selectedType === "nextcloud"
      ? osCategories.filter((cat) => cat.id === "debian" || cat.id === "ubuntu")
      : osCategories;

  const getSelectedOS = () => {
    return osCategories
      .flatMap((category) => category.versions)
      .find((os) => os.id === selectedVersion);
  };

  const handleOSSelect = (osId: string) => {
    setSelectedVersion(osId);
  };

  const handleOrderClick = (planId: string, osId: string) => {
    // Ici vous pouvez ajouter la logique pour traiter la commande
    console.log(`Commande pour le plan ${planId} avec l'OS ${osId}`);
    // Redirection vers la page de commande ou ouverture d'un modal
  };

  return (
    <div className="vps-container">
      {/* Background Effects */}
      <div className="background-overlay"></div>
      <div className="background-orb orb-1"></div>
      <div className="background-orb orb-2"></div>
      <div className="floating-particles"></div>

      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <div className="server-icon animate-bounce-slow">🖥️</div>
          <h1 className="hero-title animate-fade-in-up">
            SERVEURS VPS HAUTE PERFORMANCE
          </h1>
          <p
            className="hero-subtitle animate-fade-in-up"
            style={{ animationDelay: "0.2s" }}
          >
            Découvrez nos offres VPS adaptées à tous vos besoins : hébergement,
            développement, jeux, et plus encore. Choisissez votre système
            d'exploitation préféré et profitez de performances exceptionnelles.
          </p>
          {/* OS Selector */}
          <div
            className="os-selector animate-fade-in-up"
            style={{ animationDelay: "0.4s" }}
          >
            <div className="os-selector-title">Choisissez votre OS :</div>
            <div className="os-main-grid">
              {osCategories.map((cat) => (
                <button
                  key={cat.id}
                  className={`os-main-btn${
                    selectedCategory === cat.id ? " selected" : ""
                  }`}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setSelectedVersion(
                      cat.versions.find((v) => !v.outOfStock)?.id ||
                        cat.versions[0].id
                    );
                  }}
                  type="button"
                >
                  <span className="os-logo">
                    {cat.icon === "debian" && (
                      <svg width="28" height="28" viewBox="0 0 32 32">
                        <circle cx="16" cy="16" r="16" fill="#d70a53" />
                        <path
                          d="M23.5 13.5c-1.5-6-10-7-13.5-2.5-3.5 4.5-1.5 12 5.5 13.5 4.5 1 8-2 8-2"
                          stroke="#fff"
                          strokeWidth="2"
                          fill="none"
                        />
                      </svg>
                    )}
                    {cat.icon === "ubuntu" && (
                      <svg width="28" height="28" viewBox="0 0 32 32">
                        <circle cx="16" cy="16" r="16" fill="#e95420" />
                        <circle cx="16" cy="16" r="7" fill="#fff" />
                        <circle cx="16" cy="5" r="2" fill="#e95420" />
                        <circle cx="27" cy="21" r="2" fill="#e95420" />
                        <circle cx="5" cy="21" r="2" fill="#e95420" />
                      </svg>
                    )}
                    {cat.icon === "windows" && (
                      <svg width="28" height="28" viewBox="0 0 32 32">
                        <rect width="32" height="32" rx="16" fill="#0078d4" />
                        <rect x="6" y="8" width="8" height="7" fill="#fff" />
                        <rect x="18" y="8" width="8" height="7" fill="#fff" />
                        <rect x="6" y="17" width="8" height="7" fill="#fff" />
                        <rect x="18" y="17" width="8" height="7" fill="#fff" />
                      </svg>
                    )}
                  </span>
                  <span className="os-label">{cat.name}</span>
                </button>
              ))}
            </div>
            <div className="os-versions-grid">
              {osCategories
                .find((c) => c.id === selectedCategory)
                ?.versions.map((ver) => (
                  <button
                    key={ver.id}
                    className={`os-version-btn${
                      selectedVersion === ver.id ? " selected" : ""
                    }${ver.notAvailable ? " not-available" : ""}`}
                    onClick={() =>
                      !ver.notAvailable && setSelectedVersion(ver.id)
                    }
                    type="button"
                    disabled={ver.notAvailable}
                  >
                    {ver.label}
                    {ver.notAvailable && (
                      <span className="os-stock-badge">⏳ Bientôt</span>
                    )}
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* VPS Type Selector */}
      <div className="vps-type-selector">
        <button
          className={selectedType === "linux" ? "selected" : ""}
          onClick={() => setSelectedType("linux")}
        >
          <span role="img" aria-label="Linux">
            🐧
          </span>{" "}
          VPS Linux
        </button>
        <button
          className={selectedType === "gaming" ? "selected" : ""}
          onClick={() => setSelectedType("gaming")}
        >
          <span role="img" aria-label="Gaming">
            🎮
          </span>{" "}
          VPS Gaming
        </button>
        <button
          className={selectedType === "nextcloud" ? "selected" : ""}
          onClick={() => setSelectedType("nextcloud")}
        >
          <span role="img" aria-label="Nextcloud">
            ☁️
          </span>{" "}
          VPS Nextcloud
        </button>
      </div>

      {/* VPS Offers Section */}
      <div className="vps-section">
        <div className="vps-grid">
          {vpsPlans
            .filter((plan) => plan.type === selectedType)
            .map((plan, index) => (
              <div
                key={plan.id}
                className={`vps-card ${plan.color}${
                  plan.popular ? " popular" : ""
                } animate-fade-in-up`}
                style={{ animationDelay: `${0.1 * index}s` }}
              >
                {plan.popular && (
                  <div className="popular-badge animate-glow">Populaire</div>
                )}
                <div className="vps-card-header">
                  <div className="vps-name">{plan.name}</div>
                  <div className="vps-description">{plan.description}</div>
                </div>
                <div className="vps-card-content">
                  <div className="vps-features">
                    {plan.features.map((feature, i) => (
                      <div key={i} className="feature-row">
                        <feature.icon className="feature-icon" size={18} />
                        <div className="feature-content">
                          <span className="feature-label">{feature.label}</span>
                          <span className="feature-value">{feature.value}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="os-info">
                    <span className={`os-icon ${selectedCategory}`}>
                      {
                        osCategories.find((c) => c.id === selectedCategory)
                          ?.name[0]
                      }
                    </span>
                    <span className="selected-os">
                      <span className="selected-os-name">
                        {
                          osCategories
                            .find((c) => c.id === selectedCategory)
                            ?.versions.find((v) => v.id === selectedVersion)
                            ?.label
                        }
                      </span>
                    </span>
                  </div>
                </div>
                <div className="vps-pricing">
                  <div className="price-display">
                    <span className="price">{plan.price.toFixed(2)} €</span>
                    <span className="duration">/mois</span>
                  </div>
                </div>
                {(() => {
                  const versionObj = osCategories
                    .find((c) => c.id === selectedCategory)
                    ?.versions.find((v) => v.id === selectedVersion);
                  if (plan.notAvailable) {
                    return (
                      <button className="vps-button out-of-stock" disabled>
                        ⏳ Bientôt disponible
                      </button>
                    );
                  } else if (versionObj?.outOfStock) {
                    return (
                      <button className="vps-button out-of-stock" disabled>
                        ❌ En rupture de stock
                      </button>
                    );
                  } else {
                    return (
                      <button
                        className="vps-button animate-button-hover"
                        onClick={() =>
                          handleOrderClick(plan.id, selectedVersion)
                        }
                      >
                        <span>Commander</span>
                        <div className="button-shine"></div>
                      </button>
                    );
                  }
                })()}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default VPS;
